import React, { useState, useRef } from "react";
import { FileText, Eye, ArrowLeft } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import InvoiceForm from "./components/InvoiceForm";
import InvoicePaper from "./components/InvoicePaper";
import "./App.css";

const generateInvoiceNumber = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `INV-${year}${month}-${rand}`;
};

const getToday = () => new Date().toISOString().split("T")[0];
const getDueDate = () =>
  new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

// Detect restricted in-app browsers
const isInstagramBrowser = () => /Instagram/i.test(navigator.userAgent);

export const createDefaultData = () => ({
  senderName: "",
  businessName: "",
  logo: null,
  clientName: "",
  clientEmail: "",
  invoiceNumber: generateInvoiceNumber(),
  invoiceDate: getToday(),
  dueDate: getDueDate(),
  currency: "INR",
  items: [{ id: Date.now(), description: "", qty: 1, rate: 0 }],
  gstEnabled: true,
  gstRate: 18,
  paymentTerms:
    "Payment due within 30 days of invoice date. Please transfer to bank account details provided separately.",
  thankYouNote:
    "Thank you for your business. We look forward to working with you again.",
  bankEnabled: false,
  bankHolderName: "",
  bankAccountNumber: "",
  bankName: "",
  bankIFSC: "",
  bankSWIFT: "",
  upiEnabled: false,
  upiQR: null,
  brandColorEnabled: false,
  brandColor: "#002FA7",
});

export const formatCurrency = (amount, currency = "INR") =>
  new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: currency === "JPY" ? 0 : 2,
  }).format(amount);

export default function App() {
  const [data, setData] = useState(createDefaultData);
  const [downloading, setDownloading] = useState(false);
  const [mobileTab, setMobileTab] = useState("form");
  const [pngOverlayUrl, setPngOverlayUrl] = useState(null);
  const printRef = useRef(null);

  const formatINR = (amount) => formatCurrency(amount, data.currency || "INR");

  const handleDownload = async () => {
    setDownloading(true);

    // 1. Instagram/In-App Browser -> PNG Image Overlay
    if (isInstagramBrowser()) {
      try {
        const previewPanel = document.querySelector(".preview-panel");
        const wasHidden = previewPanel?.classList.contains("mobile-hidden");
        if (wasHidden) previewPanel.classList.remove("mobile-hidden");

        const invoiceScaler = document.querySelector(".invoice-scaler");
        let originalTransform = "";
        let originalMarginLeft = "";
        let originalMarginBottom = "";

        if (invoiceScaler) {
          originalTransform = invoiceScaler.style.transform;
          originalMarginLeft = invoiceScaler.style.marginLeft;
          originalMarginBottom = invoiceScaler.style.marginBottom;

          invoiceScaler.style.transform = "none";
          invoiceScaler.style.marginLeft = "0";
          invoiceScaler.style.marginBottom = "0";
        }

        const canvas = await html2canvas(printRef.current, {
          scale: 3,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
          windowWidth: 794,
        });

        if (invoiceScaler) {
          invoiceScaler.style.transform = originalTransform;
          invoiceScaler.style.marginLeft = originalMarginLeft;
          invoiceScaler.style.marginBottom = originalMarginBottom;
        }
        if (wasHidden) previewPanel.classList.add("mobile-hidden");

        const dataUrl = canvas.toDataURL("image/png");
        setPngOverlayUrl(dataUrl);
      } catch (err) {
        console.error("PNG generation error:", err);
        toast.error("Failed to generate image. Please try again.");
      } finally {
        setDownloading(false);
      }
      return;
    }

    // 2. Standard Browser -> Direct PDF Download
    try {
      const previewPanel = document.querySelector(".preview-panel");
      const wasHidden = previewPanel?.classList.contains("mobile-hidden");
      
      // Temporarily show preview panel if hidden (on mobile Edit tab)
      if (wasHidden) previewPanel.classList.remove("mobile-hidden");

      const invoiceScaler = document.querySelector(".invoice-scaler");
      let originalTransform = "";
      let originalMarginLeft = "";
      let originalMarginBottom = "";

      // Reset scaling to ensure full resolution capture
      if (invoiceScaler) {
        originalTransform = invoiceScaler.style.transform;
        originalMarginLeft = invoiceScaler.style.marginLeft;
        originalMarginBottom = invoiceScaler.style.marginBottom;

        invoiceScaler.style.transform = "none";
        invoiceScaler.style.marginLeft = "0";
        invoiceScaler.style.marginBottom = "0";
      }

      // Capture high-res canvas
      const canvas = await html2canvas(printRef.current, {
        scale: 2, // 2x scale for sharp text
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: 794, // Force A4 pixel width
      });

      // Restore UI state
      if (invoiceScaler) {
        invoiceScaler.style.transform = originalTransform;
        invoiceScaler.style.marginLeft = originalMarginLeft;
        invoiceScaler.style.marginBottom = originalMarginBottom;
      }
      if (wasHidden) previewPanel.classList.add("mobile-hidden");

      // Convert to PDF
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Invoice-${data.invoiceNumber}.pdf`);
      
      toast.success("Downloaded Invoice PDF");

    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => setData(createDefaultData());

  const subtotal = data.items.reduce(
    (sum, i) => sum + (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0),
    0
  );
  const gst = data.gstEnabled ? subtotal * ((parseFloat(data.gstRate) || 0) / 100) : 0;
  const grandTotal = subtotal + gst;

  return (
    <div className="app-shell">
      <aside
        className={`form-panel${mobileTab === "preview" ? " mobile-hidden" : ""}`}
        data-testid="form-panel"
      >
        <InvoiceForm
          data={data}
          setData={setData}
          onPrint={handleDownload}
          onReset={handleReset}
          downloading={downloading}
          subtotal={subtotal}
          gst={gst}
          grandTotal={grandTotal}
          formatINR={formatINR}
        />
      </aside>

      <main
        className={`preview-panel${mobileTab === "form" ? " mobile-hidden" : ""}`}
        data-testid="preview-panel"
      >
        {isInstagramBrowser() && (
          <button 
            className="insta-back-btn"
            onClick={() => setMobileTab("form")}
          >
            <ArrowLeft size={16} /> Back to Edit
          </button>
        )}
        <div className="preview-label">LIVE PREVIEW</div>
        <div className="invoice-scaler-clip">
          <div className="invoice-scaler">
            <InvoicePaper
              ref={printRef}
              data={data}
              subtotal={subtotal}
              gst={gst}
              grandTotal={grandTotal}
              formatINR={formatINR}
            />
          </div>
        </div>
      </main>

      <nav className="mobile-tab-bar">
        <button
          className={`mobile-tab${mobileTab === "form" ? " mobile-tab-active" : ""}`}
          onClick={() => setMobileTab("form")}
        >
          <FileText size={18} />
          <span>Edit</span>
        </button>
        <button
          className={`mobile-tab${mobileTab === "preview" ? " mobile-tab-active" : ""}`}
          onClick={() => setMobileTab("preview")}
        >
          <Eye size={18} />
          <span>Preview</span>
        </button>
      </nav>

      {pngOverlayUrl && (
        <div className="png-overlay" onClick={() => setPngOverlayUrl(null)}>
          <div
            className="png-overlay-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="png-overlay-banner">
              For better quality PDF, open this page in Safari or Chrome
            </div>
            <p className="png-overlay-tip">Long press the image below to save it</p>
            <img
              src={pngOverlayUrl}
              alt="Your Invoice"
              className="png-overlay-img"
            />
            <button
              className="png-overlay-close"
              onClick={() => setPngOverlayUrl(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <Toaster position="top-right" />
    </div>
  );
}